import { useEffect, useState, useRef } from 'react';
import { Plus, Pencil, Trash2, X, Check, Upload, Image, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import AdminSidebar from '../components/AdminSidebar';
import api from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminIdeas = () => {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    tagline: '',
    description: '',
    category: '',
    tech_stack: '',
    status: 'available',
    buy_price: '',
    invest_price: '',
    featured: false,
    image_url: '',
    document_url: '',
  });

  const categories = [
    'AI & Machine Learning',
    'Blockchain & Web3',
    'Enterprise Software',
    'SaaS Platform',
    'Mobile App',
    'Cybersecurity',
    'Data Analytics',
    'IoT',
    'Fintech',
    'HealthTech',
  ];

  useEffect(() => {
    fetchIdeas();
  }, []);

  const fetchIdeas = async () => {
    try {
      const response = await api.get('/ideas');
      setIdeas(response.data);
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      tagline: '',
      description: '',
      category: '',
      tech_stack: '',
      status: 'available',
      buy_price: '',
      invest_price: '',
      featured: false,
      image_url: '',
      document_url: '',
    });
    setEditingIdea(null);
  };

  const openModal = (idea = null) => {
    if (idea) {
      setEditingIdea(idea);
      setFormData({
        title: idea.title,
        tagline: idea.tagline,
        description: idea.description,
        category: idea.category,
        tech_stack: idea.tech_stack?.join(', ') || '',
        status: idea.status,
        buy_price: idea.buy_price || '',
        invest_price: idea.invest_price || '',
        featured: idea.featured,
        image_url: idea.image_url || '',
        document_url: idea.document_url || '',
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleFileUpload = async (file, fileType) => {
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('file_type', fileType);

    try {
      const response = await api.post('/admin/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (fileType === 'image') {
        setFormData(prev => ({ ...prev, image_url: response.data.url }));
        toast.success('Image uploaded successfully');
      } else {
        setFormData(prev => ({ ...prev, document_url: response.data.url }));
        toast.success('Document uploaded successfully');
      }
    } catch (error) {
      toast.error(`Upload failed: ${error.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      tech_stack: formData.tech_stack.split(',').map(t => t.trim()).filter(Boolean),
      buy_price: formData.buy_price ? parseFloat(formData.buy_price) : null,
      invest_price: formData.invest_price ? parseFloat(formData.invest_price) : null,
    };

    try {
      if (editingIdea) {
        await api.put(`/admin/ideas/${editingIdea.id}`, payload);
        toast.success('Idea updated successfully!');
      } else {
        await api.post('/admin/ideas', payload);
        toast.success('Idea created successfully!');
      }
      fetchIdeas();
      closeModal();
    } catch (error) {
      toast.error('Failed to save idea');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this idea?')) return;
    
    try {
      await api.delete(`/admin/ideas/${id}`);
      toast.success('Idea deleted');
      fetchIdeas();
    } catch (error) {
      toast.error('Failed to delete idea');
    }
  };

  const statusColors = {
    available: 'bg-emerald-500/20 text-emerald-400',
    in_development: 'bg-amber-500/20 text-amber-400',
    sold: 'bg-slate-500/20 text-slate-400',
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-ideas-page">
      <AdminSidebar />
      
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Manage Ideas</h1>
            <p className="text-slate-400">Create, edit, and manage your software ideas</p>
          </div>
          <Button
            onClick={() => openModal()}
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
            data-testid="add-idea-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Idea
          </Button>
        </div>

        {/* Ideas Table */}
        <div className="rounded-xl bg-slate-900/50 border border-white/10 overflow-hidden" data-testid="ideas-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-slate-900/50">
                <th className="text-left p-4 text-sm font-medium text-slate-400">Title</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Category</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Views</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Featured</th>
                <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td colSpan={6} className="p-4">
                      <div className="h-8 skeleton rounded" />
                    </td>
                  </tr>
                ))
              ) : ideas.length > 0 ? (
                ideas.map((idea) => (
                  <tr key={idea.id} className="border-b border-white/5 table-row-hover" data-testid={`idea-row-${idea.id}`}>
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">{idea.title}</p>
                        <p className="text-slate-500 text-sm truncate max-w-[300px]">{idea.tagline}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300 text-sm">{idea.category}</span>
                    </td>
                    <td className="p-4">
                      <Badge className={statusColors[idea.status]}>
                        {idea.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-cyan-400 font-mono text-sm">{idea.views || 0}</span>
                    </td>
                    <td className="p-4">
                      {idea.featured ? (
                        <Check className="h-5 w-5 text-emerald-400" />
                      ) : (
                        <X className="h-5 w-5 text-slate-600" />
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openModal(idea)}
                          className="text-slate-400 hover:text-white"
                          data-testid={`edit-idea-${idea.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(idea.id)}
                          className="text-slate-400 hover:text-red-400"
                          data-testid={`delete-idea-${idea.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    No ideas yet. Click "Add Idea" to create your first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        <Dialog open={modalOpen} onOpenChange={closeModal}>
          <DialogContent className="bg-slate-900 border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="idea-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingIdea ? 'Edit Idea' : 'Create New Idea'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-slate-300">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="bg-slate-950/50 border-white/10 text-white"
                    placeholder="AI-Powered Supply Chain Optimizer"
                    data-testid="idea-title-input"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-slate-300">Tagline *</Label>
                  <Input
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    required
                    className="bg-slate-950/50 border-white/10 text-white"
                    placeholder="Reduce logistics costs by 40% with predictive analytics"
                    data-testid="idea-tagline-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="bg-slate-950/50 border-white/10 text-white" data-testid="idea-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat} className="text-white">
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="bg-slate-950/50 border-white/10 text-white" data-testid="idea-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      <SelectItem value="available" className="text-white">Available</SelectItem>
                      <SelectItem value="in_development" className="text-white">In Development</SelectItem>
                      <SelectItem value="sold" className="text-white">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label className="text-slate-300">Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={5}
                    className="bg-slate-950/50 border-white/10 text-white resize-none"
                    placeholder="Detailed description of the idea, its features, and market potential..."
                    data-testid="idea-description-input"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-slate-300">Tech Stack (comma-separated)</Label>
                  <Input
                    value={formData.tech_stack}
                    onChange={(e) => setFormData({ ...formData, tech_stack: e.target.value })}
                    className="bg-slate-950/50 border-white/10 text-white"
                    placeholder="Python, TensorFlow, React, PostgreSQL"
                    data-testid="idea-tech-stack-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Buy Price (USD)</Label>
                  <Input
                    type="number"
                    value={formData.buy_price}
                    onChange={(e) => setFormData({ ...formData, buy_price: e.target.value })}
                    className="bg-slate-950/50 border-white/10 text-white"
                    placeholder="50000"
                    data-testid="idea-price-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Investment Amount (USD)</Label>
                  <Input
                    type="number"
                    value={formData.invest_price}
                    onChange={(e) => setFormData({ ...formData, invest_price: e.target.value })}
                    className="bg-slate-950/50 border-white/10 text-white"
                    placeholder="25000"
                    data-testid="idea-investment-input"
                  />
                </div>

                <div className="col-span-2 flex items-center gap-3 pt-2">
                  <Switch
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                    data-testid="idea-featured-switch"
                  />
                  <Label className="text-slate-300">Featured Idea</Label>
                </div>

                {/* Image Upload */}
                <div className="col-span-2">
                  <Label className="text-slate-300 mb-2 block">Idea Image</Label>
                  <input
                    type="file"
                    ref={imageInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'image')}
                    className="hidden"
                  />
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploading}
                      className="border-white/10 text-slate-300 hover:bg-white/5"
                      data-testid="upload-image-btn"
                    >
                      <Image className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                    {formData.image_url && (
                      <div className="flex items-center gap-2">
                        <img 
                          src={`${BACKEND_URL}${formData.image_url}`} 
                          alt="Preview" 
                          className="h-10 w-10 rounded object-cover border border-white/10"
                        />
                        <span className="text-xs text-emerald-400">Image uploaded</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({ ...formData, image_url: '' })}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Upload */}
                <div className="col-span-2">
                  <Label className="text-slate-300 mb-2 block">Attachment (PDF, DOC, PPT)</Label>
                  <input
                    type="file"
                    ref={docInputRef}
                    accept=".pdf,.doc,.docx,.txt,.ppt,.pptx"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'document')}
                    className="hidden"
                  />
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => docInputRef.current?.click()}
                      disabled={uploading}
                      className="border-white/10 text-slate-300 hover:bg-white/5"
                      data-testid="upload-doc-btn"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Document'}
                    </Button>
                    {formData.document_url && (
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-cyan-400" />
                        <span className="text-xs text-emerald-400">Document attached</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({ ...formData, document_url: '' })}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeModal}
                  className="flex-1 border border-white/10 text-slate-300"
                  data-testid="idea-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
                  data-testid="idea-save-btn"
                >
                  {editingIdea ? 'Update Idea' : 'Create Idea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminIdeas;
